(() => {
  if (window.__risePerfLoaderInitialized) return;
  window.__risePerfLoaderInitialized = true;

  const PERF_CONFIG = window.__risePerfConfig || {};
  const queues = {
    interaction: [],
    interactionOnly: [],
    idle: [],
    viewport: [],
  };
  const loaded = new Set();

  const enqueue = (phase, item) => {
    const normalizedPhase = Object.prototype.hasOwnProperty.call(queues, phase) ? phase : 'idle';
    queues[normalizedPhase].push(item);
  };

  const addScript = (src, attrs = {}, phase = 'idle') => {
    if (!src || loaded.has(src)) return;
    if (document.querySelector(`script[src="${src}"]`)) return;
    enqueue(phase, { type: 'script', src, attrs });
    loaded.add(src);
  };

  const addStyle = (href, phase = 'idle') => {
    if (!href || loaded.has(href)) return;
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
    enqueue(phase, { type: 'style', href });
    loaded.add(href);
  };

  const appendScript = ({ src, attrs }) => {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    Object.entries(attrs || {}).forEach(([key, value]) => {
      if (key === 'onload' && typeof value === 'function') {
        script.addEventListener('load', value);
        return;
      }
      if (value === true) script.setAttribute(key, '');
      else if (value !== false && value != null) script.setAttribute(key, String(value));
    });
    document.head.appendChild(script);
  };

  const appendStyle = ({ href }) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  };

  const flushQueue = (phase = 'idle') => {
    const queue = queues[phase];
    if (!queue) return;
    while (queue.length) {
      const item = queue.shift();
      if (item.type === 'style') appendStyle(item);
      else appendScript(item);
    }
  };

  const interactionEvents = ['pointerdown', 'keydown'];

  const onFirstInteraction = (callback) => {
    let done = false;
    const handler = () => {
      if (done) return;
      done = true;
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, handler, { passive: true }));
      callback();
    };
    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, handler, { passive: true, once: true })
    );
  };

  const idle = window.requestIdleCallback
    ? (callback) => window.requestIdleCallback(callback, { timeout: 2500 })
    : (callback) => window.setTimeout(callback, 1200);

  const setMerchantWidgetTitle = () => {
    const iframe =
      document.getElementById('merchantwidgetiframe') ||
      document.querySelector('iframe[src*="shopping/merchantverse"]');
    if (!iframe) return false;
    if (!iframe.getAttribute('title')) {
      iframe.setAttribute('title', 'Google shop quality widget');
    }
    return true;
  };

  const observeMerchantWidget = () => {
    if (setMerchantWidgetTitle() || !document.body) return;
    const observer = new MutationObserver(() => {
      if (setMerchantWidgetTitle()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  };

  const startMerchantWidget = () => {
    if (!window.merchantwidget || window.__riseMerchantWidgetStarted) return false;
    window.__riseMerchantWidgetStarted = true;
    window.merchantwidget.start({
      position: 'LEFT_BOTTOM',
      sideMargin: '36',
      bottomMargin: '60',
      mobileSideMargin: '16',
      mobileBottomMargin: '70',
      merchant_id: PERF_CONFIG.merchantId,
    });
    observeMerchantWidget();
    return true;
  };

  if (Array.isArray(window.__riseDeferredThirdPartySrcs)) {
    window.__riseDeferredThirdPartySrcs.forEach((src) => addScript(src, {}, 'idle'));
  }

  if (window.__riseDeferredThirdPartyAssets) {
    const deferredAssets = window.__riseDeferredThirdPartyAssets;
    Object.keys(deferredAssets).forEach((phase) => {
      const assets = deferredAssets[phase] || {};
      (assets.styles || []).forEach((href) => addStyle(href, phase));
      (assets.scripts || []).forEach((src) => addScript(src, {}, phase));
    });
  }

  addScript(PERF_CONFIG.toastibarScriptSrc, {}, 'interaction');

  const shouldLoadMerchantWidget = PERF_CONFIG.isProduct || PERF_CONFIG.isCollection;

  if (shouldLoadMerchantWidget) {
    addScript(
      PERF_CONFIG.merchantWidgetScriptSrc,
      { id: 'merchantWidgetScript', onload: startMerchantWidget },
      'interactionOnly'
    );
    window.__riseStartMerchantWidget = startMerchantWidget;
  }

  let idleLoadStarted = false;
  let interactionLoadScheduled = false;
  let interactionLoadFlushed = false;
  let interactionOnlyLoadFlushed = false;

  const startIdleLoad = () => {
    if (idleLoadStarted) return;
    idleLoadStarted = true;
    idle(() => flushQueue('idle'));
  };

  const flushInteractionLoad = ({ includeInteractionOnly = false } = {}) => {
    if (interactionLoadFlushed && (!includeInteractionOnly || interactionOnlyLoadFlushed)) return;
    idle(() => {
      if (!interactionLoadFlushed) {
        interactionLoadFlushed = true;
        flushQueue('interaction');
        startIdleLoad();
      }
      if (includeInteractionOnly && !interactionOnlyLoadFlushed) {
        interactionOnlyLoadFlushed = true;
        flushQueue('interactionOnly');
      }
    });
  };

  const startInteractionLoad = () => {
    if (interactionLoadScheduled) return;
    interactionLoadScheduled = true;
    window.setTimeout(() => flushInteractionLoad({ includeInteractionOnly: true }), PERF_CONFIG.interactionDelayMs);
  };

  const startViewportLoad = () => {
    flushQueue('viewport');
  };

  if (queues.viewport.length) {
    const viewportTarget =
      document.querySelector('[data-rise-deferred-assets-trigger]') ||
      document.querySelector('.shopify-app-block, [id*="shopify-block"], [id*="ProductReviews"], [class*="yotpo"]');
    if (viewportTarget && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) return;
          observer.disconnect();
          startViewportLoad();
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(viewportTarget);
    } else {
      window.setTimeout(startViewportLoad, PERF_CONFIG.viewportFallbackMs);
    }
  }

  onFirstInteraction(startInteractionLoad);
  window.setTimeout(() => flushInteractionLoad(), PERF_CONFIG.fallbackDelayMs);
})();
